import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import TableRowsRoundedIcon from "@mui/icons-material/TableRowsRounded";
import UnfoldMoreRoundedIcon from "@mui/icons-material/UnfoldMoreRounded";
import UnfoldLessRoundedIcon from "@mui/icons-material/UnfoldLessRounded";
import { Box, Typography, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { requestMarketData } from "../../Redux/Reducers/marketDataSlice";
import { applySortFilter, getComparator } from "../../component/Methods";
import { StrategiesDashboard } from "../../component/Methods/dashboardCalculations";
import { getMarketDataThreshold } from "../../Redux/Reducers/selectors";
import { useVantageSystemState } from "../../hooks/useOptimizedState";
import ControlBar from "./components/ControlBar";
import PortfolioHero from "./components/PortfolioHero";
import BenchmarkTicker from "./components/BenchmarkTicker";
import StrategyCardGrid from "./components/StrategyCardGrid";
import StrategyPerformanceTable from "./components/StrategyPerformanceTable";

const PerformanceDashboardV2 = () => {
  const threshold = useSelector(getMarketDataThreshold);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("plPer");
  const [hideZeroAum, setHideZeroAum] = useState(true);
  const [selectedFundLabel, setSelectedFundLabel] = useState(null);
  const [strategyQuery, setStrategyQuery] = useState("");
  const [cardsCollapsed, setCardsCollapsed] = useState(false);
  const [strategiesView, setStrategiesView] = useState("table");

  const { funds, trades, lots, livePrices, RawMarketData, accountingData } =
    useVantageSystemState();

  const resultArray = StrategiesDashboard();

  const sortedRows = useMemo(
    () => applySortFilter(resultArray, getComparator(order, orderBy), ""),
    [resultArray, order, orderBy]
  );

  const timeoutRef = useRef(null);
  const [isFirstRender, setIsFirstRender] = useState(true);

  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      dispatch(
        requestMarketData({
          lots,
          threshold,
          noRequest: true,
          trades,
        })
      );
    }, 20000);

    return () => clearTimeout(timeoutRef.current);
    // Match classic dashboard: re-run when RawMarketData changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [RawMarketData]);

  const findFundAum = (key, array) => {
    if (key && array && array[0]?.funds) {
      const findAum = array[0]?.funds?.find((i) => i.fund === key);
      return findAum?.aum;
    }
  };

  const accFund = useMemo(() => {
    if (!funds) return [];

    return funds.map((fund) => {
      const strategyRows =
        resultArray?.filter((row) => row.fundName === fund.abbreviation) || [];
      const totalPL = strategyRows.reduce(
        (total, row) => total + (parseFloat(row.pL) || 0),
        0
      );
      const entryAUM = parseFloat(findFundAum(fund.fundId, accountingData)) || 0;

      return {
        name: fund.name,
        abbreviation: fund.abbreviation,
        aum: entryAUM,
        size: totalPL,
        pf: entryAUM > 0 ? (totalPL / entryAUM) * 100 : 0,
      };
    });
  }, [funds, resultArray, accountingData]);

  const fundSummaries = useMemo(() => {
    const fromAcc = new Map((accFund || []).map((f) => [f.abbreviation, f]));
    const names = new Set();
    sortedRows?.forEach((row) => {
      if (row.fundName) names.add(row.fundName);
    });
    accFund?.forEach((f) => {
      if (f.abbreviation) names.add(f.abbreviation);
    });

    return Array.from(names)
      .map((abbr) => {
        const existing = fromAcc.get(abbr);
        if (existing) return existing;

        const strategyRows =
          resultArray?.filter((row) => row.fundName === abbr) || [];
        const totalPL = strategyRows.reduce(
          (total, row) => total + (parseFloat(row.pL) || 0),
          0
        );
        const fundMatch = funds?.find((f) => f.abbreviation === abbr);
        const entryAUM = fundMatch
          ? parseFloat(findFundAum(fundMatch.fundId, accountingData)) || 0
          : 0;

        return {
          name: fundMatch?.name || abbr,
          abbreviation: abbr,
          aum: entryAUM,
          size: totalPL,
          pf: entryAUM > 0 ? (totalPL / entryAUM) * 100 : 0,
        };
      })
      .sort((a, b) => (Number(b.aum) || 0) - (Number(a.aum) || 0));
  }, [accFund, sortedRows, resultArray, funds, accountingData]);

  // Clear stale fund selection if that fund disappears from the list
  useEffect(() => {
    if (!selectedFundLabel) return;
    const stillValid = fundSummaries.some(
      (f) => f.abbreviation === selectedFundLabel
    );
    if (!stillValid) {
      setSelectedFundLabel(null);
      setStrategyQuery("");
    }
  }, [fundSummaries, selectedFundLabel]);

  const fundRows = useMemo(() => {
    return (sortedRows || []).filter((row) => {
      const meetsAumCondition =
        (hideZeroAum && parseFloat(row?.aum) > 10) || !hideZeroAum;
      const meetsFundCondition =
        !selectedFundLabel || row.fundName === selectedFundLabel;
      return meetsAumCondition && meetsFundCondition;
    });
  }, [sortedRows, hideZeroAum, selectedFundLabel]);

  const strategyOptions = useMemo(() => {
    const names = new Set();
    fundRows?.forEach((row) => {
      if (row.strategyName) names.add(row.strategyName);
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [fundRows]);

  const filteredRows = useMemo(() => {
    const q = strategyQuery.trim().toLowerCase();
    if (!q) return fundRows || [];
    return (fundRows || []).filter((row) =>
      row.strategyName?.toLowerCase().includes(q)
    );
  }, [fundRows, strategyQuery]);

  const handleFundSelect = useCallback((abbr) => {
    setSelectedFundLabel(abbr || null);
    setStrategyQuery("");
  }, []);

  const handleRequestSort = useCallback((property) => {
    setOrder((prevOrder) =>
      orderBy === property && prevOrder === "desc" ? "asc" : "desc"
    );
    setOrderBy(property);
  }, [orderBy]);

  const heroTotals = useMemo(() => {
    const rows = fundRows || [];
    const scopedSummaries = selectedFundLabel
      ? fundSummaries.filter((f) => f.abbreviation === selectedFundLabel)
      : fundSummaries;

    const totalAum = scopedSummaries.reduce(
      (sum, f) => sum + (Number(f.aum) || 0),
      0
    );
    const totalPL = rows.reduce((sum, r) => sum + (parseFloat(r.pL) || 0), 0);
    const todayReturn = totalAum > 0 ? totalPL / totalAum : 0;
    const blendedPlPer = todayReturn * 100;

    // Single fund: use that fund's performance. All funds: AUM-weighted RoR.
    const weightedPerf = { Net_RoR: 0, MTD_RoR: 0, QTD_RoR: 0, YTD_RoR: 0, ITD_RoR: 0 };
    let weightSum = 0;
    scopedSummaries.forEach((summary) => {
      const fundMatch = funds?.find((f) => f.abbreviation === summary.abbreviation);
      const accountingFund = accountingData?.[0]?.funds?.find(
        (f) => f.fund === fundMatch?.fundId
      );
      const perf = accountingFund?.performance;
      const aum = Number(summary.aum) || 0;
      if (!perf || aum <= 0) return;
      weightSum += aum;
      weightedPerf.Net_RoR += aum * (Number(perf.Net_RoR) || 0);
      weightedPerf.MTD_RoR += aum * (Number(perf.MTD_RoR) || 0);
      weightedPerf.QTD_RoR += aum * (Number(perf.QTD_RoR) || 0);
      weightedPerf.YTD_RoR += aum * (Number(perf.YTD_RoR) || 0);
      weightedPerf.ITD_RoR += aum * (Number(perf.ITD_RoR) || 0);
    });

    const perf =
      weightSum > 0
        ? {
            Net_RoR: weightedPerf.Net_RoR / weightSum,
            MTD_RoR: weightedPerf.MTD_RoR / weightSum,
            QTD_RoR: weightedPerf.QTD_RoR / weightSum,
            YTD_RoR: weightedPerf.YTD_RoR / weightSum,
            ITD_RoR: weightedPerf.ITD_RoR / weightSum,
          }
        : null;

    const periodWithToday = (ror) => {
      if (ror == null || !Number.isFinite(Number(ror))) return undefined;
      return ((1 + Number(ror)) * (1 + todayReturn) - 1) * 100;
    };

    return {
      totalAum,
      totalPL,
      periodPercents: {
        yest:
          perf?.Net_RoR != null && Number.isFinite(Number(perf.Net_RoR))
            ? Number(perf.Net_RoR) * 100
            : undefined,
        today: blendedPlPer,
        mtd: periodWithToday(perf?.MTD_RoR),
        qtd: periodWithToday(perf?.QTD_RoR),
        ytd: periodWithToday(perf?.YTD_RoR),
        itd: periodWithToday(perf?.ITD_RoR),
      },
      strategyCount: rows.length,
    };
  }, [fundRows, fundSummaries, selectedFundLabel, funds, accountingData]);

  const handleFundClick = useCallback(
    (fundName) => {
      navigate(`/portfolio/${fundName}`);
    },
    [navigate]
  );

  const handleStrategyClick = useCallback(
    (fundName, strategyName) => {
      navigate(`/portfolio/${fundName}/${strategyName}`);
    },
    [navigate]
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2.5,
        color: "text.primary",
      }}
    >
      <PortfolioHero
        totalAum={heroTotals.totalAum}
        totalPL={heroTotals.totalPL}
        periodPercents={heroTotals.periodPercents}
      />

      <ControlBar
        hideZeroAum={hideZeroAum}
        onHideZeroAumChange={(e) => setHideZeroAum(e.target.checked)}
        orderBy={orderBy}
        onOrderByChange={setOrderBy}
        order={order}
        onOrderChange={setOrder}
        strategyOptions={strategyOptions}
        strategyQuery={strategyQuery}
        onStrategyQueryChange={setStrategyQuery}
        fundSummaries={fundSummaries}
        selectedFundLabel={selectedFundLabel}
        onFundSelect={handleFundSelect}
      />

      <BenchmarkTicker marketData={livePrices} />

      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 1.5,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              letterSpacing: "0.04em",
              fontSize: "1rem",
              color: (t) =>
                t.palette.mode === "dark" ? "#D0D0D0" : "text.secondary",
            }}
          >
            Strategies
            {filteredRows?.length != null ? ` · ${filteredRows.length}` : ""}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {strategiesView === "grid" && (
              <ToggleButtonGroup
                exclusive
                size="small"
                value={cardsCollapsed ? "collapse" : "expand"}
                onChange={(_event, next) => {
                  if (next) setCardsCollapsed(next === "collapse");
                }}
                aria-label="Strategy card density"
                sx={{
                  "& .MuiToggleButton-root": {
                    px: 0.85,
                    py: 0.4,
                    minWidth: 0,
                  },
                }}
              >
                <ToggleButton value="expand" aria-label="Expand cards">
                  <Tooltip title="Expand">
                    <UnfoldMoreRoundedIcon sx={{ fontSize: "1.1rem" }} />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="collapse" aria-label="Collapse cards">
                  <Tooltip title="Collapse">
                    <UnfoldLessRoundedIcon sx={{ fontSize: "1.1rem" }} />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            )}
            <ToggleButtonGroup
              exclusive
              size="small"
              value={strategiesView}
              onChange={(_event, next) => {
                if (next) setStrategiesView(next);
              }}
              aria-label="Strategy layout"
              sx={{
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  px: 1.25,
                  py: 0.4,
                  gap: 0.5,
                },
              }}
            >
              <ToggleButton value="grid" aria-label="Card grid">
                <GridViewRoundedIcon sx={{ fontSize: "1.05rem" }} />
                Cards
              </ToggleButton>
              <ToggleButton value="table" aria-label="Table">
                <TableRowsRoundedIcon sx={{ fontSize: "1.05rem" }} />
                Table
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
        {strategiesView === "table" ? (
          <StrategyPerformanceTable
            rows={filteredRows}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            onFundClick={handleFundClick}
            onStrategyClick={handleStrategyClick}
          />
        ) : (
          <StrategyCardGrid
            rows={filteredRows}
            collapsed={cardsCollapsed}
            onFundClick={handleFundClick}
            onStrategyClick={handleStrategyClick}
          />
        )}
      </Box>
    </Box>
  );
};

export default PerformanceDashboardV2;
